import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has, have } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl,
    createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { greaterThan, lessThanOrEqualTo, required, requiredWhen, validateError } from "../../../../utils/validate";
import { createConditionForm as createConditionFormInform } from "../../inform/request/request.condition";
import { openModalForm as openModalFormConfirmApprove } from "../../storage-location/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "../../storage-location/request/modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../storage-location/request/modal/confirm-request.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import {
    callRequestDocGenType,
    callUser,
    callUserCompanyMe
} from "../../storage-location/request/requestor/requestor.section";
import { createForm as createFormInspection } from "../inspection/inspection.logic";
import { openModalForm as openModalFormUnlock } from "./modal/confirm-unlock.logic.js";
import { createConditionAction, createConditionForm } from "./request.condition";
import { callInspectionAttachment, createColumns, createField } from "./request.section";
import { callGetHazLicenseReserveAndUse } from "../../haz-mgt/buy/buy.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callUnlock = ({ data }) => store.dispatch(`${actionPrefix}/unlock`, { data });
export const callReportInspection = ({ data }) => store.dispatch(`${actionPrefix}/reportInspection`, { data });

export const callContactStaffType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "CONTACT_STAFF_TYPE", value: searchQuery });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refInspectionTemp.refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refInspectionTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refInspectionTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules3 = createRulesTab3({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "InspectionInfo";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules3));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog, prefix = "refInspectionTemp." }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                `${prefix}**`,
                `${prefix}refUserCompany.**`,
                `${prefix}refUser.**`,
                `${prefix}refInformTemp.id`,
                `${prefix}refInformTemp.approveCode`,
                `${prefix}refInformTemp.vehicleNo`,
                `${prefix}refInformTemp.containerNo`,
                `${prefix}refInformTemp.sealNo`,
                `${prefix}refInformTemp.refOperation.id`,
                `${prefix}refInformTemp.refOperation.key`,
                `${prefix}refInformTemp.refOperation.value`,
                `${prefix}refInformTemp.refInspectionLv.*`,

                `${prefix}refInformTemp.refTransportByWay.id`,
                `${prefix}refInformTemp.refTransportByWay.key`,
                `${prefix}refInformTemp.refTransportByWay.value`,
                `${prefix}refInformTemp.refOriginDestinationCountry.*`,
                `${prefix}refInformTemp.refExportCheckpoint.*`,
                `${prefix}refInformTemp.refImportCheckpoint.*`,
                `${prefix}refInformTemp.refPassCustoms.*`,

                `${prefix}refInformTemp.refTransportByWay2.id`,
                `${prefix}refInformTemp.refTransportByWay2.key`,
                `${prefix}refInformTemp.refTransportByWay2.value`,
                `${prefix}refInformTemp.refOriginDestinationCountry2.*`,
                `${prefix}refInformTemp.refExportCheckpoint2.*`,
                `${prefix}refInformTemp.refImportCheckpoint2.*`,
                `${prefix}refInformTemp.refPassCustoms2.*`,

                `${prefix}refInformTemp.refRequestDoc.id`,
                `${prefix}refInformTemp.refRequestDoc.key`,
                `${prefix}refInformTemp.refRequestDoc.value`,
                `${prefix}refInformTemp.refTransferDeclaration.id`,
                `${prefix}refInformTemp.refTransferDeclaration.declarationNumber`,
                `${prefix}refInformTemp.refTransferDeclaration.referenceNumber`,
                `${prefix}refInformTemp.refTransferDeclaration.containerNo`,
                `${prefix}refInformTemp.refTransferDeclaration.vehicleNo`,
                `${prefix}refInspection.id`,
                `${prefix}refInspection.approveCode`,
                `${prefix}refInspection.refUserCompany.*`,
                `${prefix}refInspection.refUserCompany.refPersonType.*`,
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                {
                    name: `${prefix}refUserCompany.addresses`,
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: `${prefix}refUserCompany.userObjectives`,
                    select: ["*", "refObjective.*"],
                    page: 0,
                    size: 1000000
                },
                {
                    name: `${prefix}refUser.addresses`,
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: `${prefix}products`,
                    select: ["*",
                        "refInformProduct.**",
                        "refInformProduct.refHazObject.**",
                        "refInformProduct.refCoordinateCustoms.**",
                        "refInformProduct.refTransferDeclarationProduct.**",
                    ],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: [
                        {
                            name: "refInformProduct.productQties",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                        },
                        {
                            name: "refInformProduct.refTransferDeclarationProduct.productQties",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                        },
                        {
                            name: "refInformProduct.refHaz.mainSubStances",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                        },
                        {
                            name: "productQties",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                        },
                    ]
                },
                {
                    name: `${prefix}officers`,
                    select: ["*", "refUser.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: []
                },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "feeStatements",
                    select: ["*", "refType.*", "refStatus.*"],
                    page: 0,
                    size: 1,
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: []
                },
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refInspectionTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([{ name: "refInspectionTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "forCenterAdmin" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        { name: "refRequestDoc.key", type: "String", operator: "in", value: ["20"] },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refInspection: null,
        refInspectionTemp: { ...createFormInspection() },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    const rules = {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key }),
    }
    return rules;
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    if (data.sendUnlock && !data.sendApprove) { return {}; }
    return {
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refInspection`]: [requiredWhen({ when: () => ["2", "3", "4"].includes(model.refRequestType?.key) })],
        [`${prefix}refInspectionTemp.refRequestDoc`]: [required],
        [`${prefix}refInspectionTemp.refInformTemp`]: [requiredWhen({ when: () => ["02", "16", "17", "18", "19"].includes(model.refInspectionTemp?.refRequestDoc?.key) })],

        [`${prefix}refInspectionTemp.isInspect`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" })],
        [`${prefix}refInspectionTemp.inspectRemark`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && !model.refInspectionTemp?.isRiskValidated && model.refInspectionTemp?.isInspect })],
        [`${prefix}refInspectionTemp.refInspectLocation`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.isInspect })],
        [`${prefix}refInspectionTemp.locationRemark`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.refInspectLocation?.key === "3" })],
        [`${prefix}refInspectionTemp.refSequestrate`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.isInspect })],
        [`${prefix}refInspectionTemp.refContactStaffType`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.isInspect })],
        [`${prefix}refInspectionTemp.refInspectDepartment`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.refInspectLocation?.key === "1" })],
        [`${prefix}refInspectionTemp.refStorageLocation`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.refInspectLocation?.key === "2" })],
        // [`${prefix}refInspectionTemp.officers.0.refUser`]: [requiredWhen({ when: () => model.refStatus?.key === "waitReceive" && model.refInspectionTemp?.refContactStaffType?.key === "1" && model.refInspectionTemp?.officers?.length > 0})],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refInspectionTemp.refUserCompany`]: [required],
        [`${prefix}refInspectionTemp.refUser`]: [required]
    };
};

export const createRulesTab2 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refInspectionTemp.inspectionDatetime`]: [required],
        [`${prefix}refInspectionTemp.transportDate`]: [required],
        [`${prefix}refInspectionTemp.refInspector`]: [required],
        [`${prefix}refInspectionTemp.inspectorTel`]: [required],
        [`${prefix}refInspectionTemp.containerNo`]: [required],
        [`${prefix}refInspectionTemp.vehicleNo`]: [required],
        [`${prefix}refInspectionTemp.sealNo`]: [requiredWhen({
            when: () => {
                return ["16", "17", "18", "19"].includes(model?.refInspectionTemp?.refRequestDoc?.key) && (!model.createDate || moment(model.createDate).isAfter(moment("2024-12-12T17:00:00:000000Z")));
            }
        })],
        [`${prefix}refInspectionTemp.refInformTemp.products`]: [
            {
                validator: (rule, value) => {
                    if (value?.every(d => !d.isCheck)) {
                        return Promise.reject(i18n.global.t("views.request.inspectionInfo.error.isCheckAtLessOneRecord"));
                    }
                    return Promise.resolve();
                },
                trigger: "change"
            }
        ]
    };

    model?.refInspectionTemp?.refInformTemp?.products?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.isCheck`] = [required];
            item.productQties?.forEach((productQty, index2) => {
                if (productQty.isDelete !== true) {
                    if (productQty.docName === i18n.global.t("model.informProductQty.labels.transferDeclarationDoc")) {
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.qty`] = [];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.netWeight`] = [];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.numberOfPackage`] = [];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.packageSize`] = [];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.amount`] = [];
                    } else {
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.qty`] = [required];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.netWeight`] = [required];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.numberOfPackage`] = [required];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.packageSize`] = [required];
                        rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.amount`] = [required];
                        if (productQty.docName === i18n.global.t("model.informProductQty.labels.inspectionDoc") && productQty?.refInformProductQty) {
                            const informProductQty = productQty?.refInformProductQty;
                            rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.qty`].push(lessThanOrEqualTo(!item.isCheck ? 0 : informProductQty?.qty || 0));
                            rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.netWeight`].push(lessThanOrEqualTo(!item.isCheck ? 0 : informProductQty?.netWeight || 0));
                            rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.numberOfPackage`].push(lessThanOrEqualTo(!item.isCheck ? 0 : informProductQty?.numberOfPackage || 0));
                            rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.packageSize`].push(lessThanOrEqualTo(!item.isCheck ? 0 : informProductQty?.packageSize || 0));
                            if (!item.isCheck) {
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.amount`].push(lessThanOrEqualTo(0));
                            }
                            if (item.isCheck) {
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.qty`].push(greaterThan(0));
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.netWeight`].push(greaterThan(0));
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.numberOfPackage`].push(greaterThan(0));
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.packageSize`].push(greaterThan(0));
                                rules[`${prefix}refInspectionTemp.refInformTemp.products.${index}.productQties.${index2}.amount`].push(greaterThan(0));
                            }

                        }
                    }
                }
            });
        }
    });
    return rules;
};

export const createRulesTab3 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refInspectionTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refInspectionTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refInspectionTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => (model.refInspectionTemp.attachments[index].attribute?.isRequiredAttachment || model.refInspectionTemp.attachments[index].attribute?.documentNo) })
            ];
            rules[`${prefix}refInspectionTemp.attachments.${index}.attribute.documentNo`] = [
                requiredWhen({ when: () => (model.refInspectionTemp.attachments[index].attribute?.isRequiredDocumentNo) })
            ];
            rules[`${prefix}refInspectionTemp.attachments.${index}.attribute.documentDate`] = [
                requiredWhen({ when: () => (model.refInspectionTemp.attachments[index].attribute?.isRequiredDocumentDate) })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callInspectionAttachment({}),
        callRequestStatus({}),
        callRequestType({}),
        callRequestDoc({}),
        callRequestDocGenType({}),
        callContactStaffType({}),
    ];
    return Promise.all(promises).then(arr => {
        const userInfo = store.state.resource.auth.info?.user;
        data.inspectionAttachments = arr[0];
        data.statuses = arr[1];
        data.requestTypes = arr[2];
        data.requestDocs = arr[3];
        data.requestDocGenTypes = arr[4];
        data.contactStaffTypes = arr[5];

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            data.inspectionAttachments.forEach(item =>
                modelRef.refInspectionTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
            modelRef.refRequestType = data.requestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === "20");
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refInspectionTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => {
                                modelRef.refInspectionTemp.refUser = users.find(d => d.id === userInfo?.id)
                                modelRef.refInspectionTemp.refInspector = users.find(d => d.id === userInfo?.id)
                            }
                        );
                    }
                });
            }
        }

        if (modelRef?.refInspectionTemp?.products?.length) {
            modelRef.refInspectionTemp.refInformTemp.products = cloneInformTempProducts({ modelRef, products: modelRef.refInspectionTemp?.products });
            modelRef.refInspectionTemp.refInformTemp.products?.forEach?.(p => {
                if (p.refHazLicense?.id) {
                    callGetHazLicenseReserveAndUse({ hazLicenseId: [p.refHazLicense?.id] })
                        .then(hazLicenseReserveUses => {
                            const reserve = lodash.sum(hazLicenseReserveUses.map(d => d.reserve || 0));
                            const use = lodash.sum(hazLicenseReserveUses.map(d => d.use || 0));
                            p.refHazLicense.quantityReserve = reserve;
                            p.refHazLicense.quantityUse = use;
                        });
                }
            })
        }
    });
};

export const cloneInformTempProducts = ({ modelRef, products, enable = true }) => {
    return products?.map(p => {
        p.refInformProduct.isCheck = p.isCheck;
        p.refInformProduct.setIsCheck = setIsCheck;
        const inspectionProduct = products?.find(d => d.refInformProduct?.id === p.refInformProduct?.id);
        p.refInformProduct.productQties.forEach(d => d.docName = i18n.global.t("model.informProductQty.labels.referDoc"));
        if (p.refInformProduct?.refTransferDeclarationProduct?.productQties?.length) {
            p.refInformProduct.refTransferDeclarationProduct.productQties.forEach(d => {
                d.docName = i18n.global.t("model.informProductQty.labels.transferDeclarationDoc");
                p.refInformProduct.productQties.push(d)
            });
        }
        if (inspectionProduct?.productQties?.length) {
            inspectionProduct?.productQties.forEach(d => {
                d.enable = enable && modelRef?.refRequestType?.key !== "4" ;
                d.setProductQty = setProductQty;
                d.docName = i18n.global.t("model.informProductQty.labels.inspectionDoc");
            });
            p.refInformProduct.productQties.push(...inspectionProduct.productQties)
        }
        return p.refInformProduct;
    })
};

export const setIsCheck = ({ modelRef, id, checked }) => {
    const product = modelRef.refInspectionTemp?.products?.find(p => p.refInformProduct?.id === id);
    if (product) {
        product.isCheck = checked;
        product.isSave = true;
        modelRef.refInspectionTemp.isSave = true;
    }
};

export const setIsSequestrate = ({ modelRef, id, checked }) => {
    const product = modelRef.refReleaseTemp?.products?.find(p => p.refInspectionProduct.refInformProduct?.id === id);
    if (product) {
        product.isSequestrate = checked;
        product.isSave = true;
        modelRef.refReleaseTemp.isSave = true;
    }
};

export const setIsSampling = ({ modelRef, id, checked }) => {
    const product = modelRef.refReleaseTemp?.products?.find(p => p.refInspectionProduct.refInformProduct?.id === id);
    if (product) {
        product.isSampling = checked;
        product.isSave = true;
        modelRef.refReleaseTemp.isSave = true;
    }
};

export const setSequestrates = ({ modelRef, id, sequestrates }) => {
    const product = modelRef.refReleaseTemp?.products?.find(p => p.refInspectionProduct.refInformProduct?.id === id);
    if (product) {
        product.sequestrates = sequestrates;
        product.isSave = true;
        modelRef.refReleaseTemp.isSave = true;
    }
};

export const setSamplings = ({ modelRef, id, samplings }) => {
    const product = modelRef.refReleaseTemp?.products?.find(p => p.refInspectionProduct.refInformProduct?.id === id);
    if (product) {
        product.samplings = samplings;
        product.isSave = true;
        modelRef.refReleaseTemp.isSave = true;
    }
};

export const setProductQty = ({ modelRef, record, informProduct, productsKey = "refInspectionTemp.products", productKey = "refInformProduct" }) => {
    const products = lodash.get(modelRef, productsKey);
    const product = products?.find(p => lodash.get(p, productKey)?.id === informProduct?.id && !p.isDelete);
    const productQty = product?.productQties?.find(q => (q.id === record?.id || q.refInformProductQty?.id === record?.id) && !q.isDelete);
    if (productQty) {
        if (product.refInformProduct) { product.refInformProduct.isSave = false; }
        if (product.refInspectionProduct) { product.refInspectionProduct.isSave = false; }
        if (productQty.refInformProductQty) { productQty.refInformProductQty.isSave = false; }
        if (productQty.refInspectionProductQty) { productQty.refInspectionProductQty.isSave = false; }
        product.isSave = true;
        productQty.isSave = true;
        productQty.amount = record.amount;
        productQty.netWeight = record.netWeight;
        productQty.numberOfPackage = record.numberOfPackage;
        productQty.packageSize = record.packageSize;
        productQty.qty = record.qty;
    }
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction?.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction?.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction?.canEdit({ props, record })
        }),
        createRowActionEdit({
            bind: { class: "btn-purple", size: "small", icon: () => <CIcon file="change-password.svg"></CIcon> },
            text: () => i18n.global.t("button.unlock"),
            showText: () => false,
            menuContext,
            onClick: ({ record }) => openModalFormUnlock({
                onOk: form => {
                    setIsLoadingApp(true);
                    callUnlock({
                        data: {
                            id: record.id,
                            version: record.version,
                            refInspectionTemp: {
                                id: record.refInspectionTemp?.id,
                                version: record.refInspectionTemp?.version,
                                ...form,
                                isUnlock: true
                            }
                        }
                    })
                        .finally(() => setIsLoadingApp(false))
                        .then(() => {
                            notification["success"]({ message: i18n.global.t("views.request.modal.receive.unlockSuccess") })
                            onSearch({ props, page: state.page })
                        });
                }
            }),
            visible: ({ record }) => state.conditionAction?.canUnlock({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction?.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportInspection({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction?.canReport({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = [
        ...createConditionForm({ props, data, refs, isLoading }),
        ...createConditionFormInform({ dataKey: "refInspectionTemp" }),
    ];
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refInspectionTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab3({ props, data, refs }),
        itemsKey: "refInspectionTemp.attachments"
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refInspectionTemp.products.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab2({ props, data, refs }),
        itemsKey: "refInspectionTemp.products"
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refInspectionTemp.refInformTemp?.products?.filter(d => d.isDelete !== true && d.isCheck)?.length,
        createRules: () => createRulesTab2({ props, data, refs }),
        itemsKey: "refInspectionTemp.refInformTemp.products"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive, feeStatements } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canUnlock = state.conditionAction.canUnlock({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refInspectionTemp = modelRef.refInspectionTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        formParams: {
                            refInspectionTemp: {
                                ...modelRef.refInspectionTemp,
                                refUserCompany: refInspectionTemp?.refUserCompany?.id ? refInspectionTemp?.refUserCompany : null,
                                refUser: refInspectionTemp?.refUser?.id ? refInspectionTemp?.refUser : null
                            },
                            statusRemark
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmRequest({
                            onOk: record => {
                                const refInspectionTemp = modelRef.refInspectionTemp;
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refInspectionTemp: {
                                            ...modelRef.refInspectionTemp,
                                            refUserCompany: refInspectionTemp?.refUserCompany?.id ? refInspectionTemp?.refUserCompany : null,
                                            refUser: refInspectionTemp?.refUser?.id ? refInspectionTemp?.refUser : null
                                        },
                                        statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                        refStatus: statusWaitReceive,
                                        isSignCa: record.isSignCa,
                                        passwordCa: record.passwordCa
                                    },
                                    successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canUnlock) {
        col1.push(
            createButton({
                text: i18n.global.t("button.unlock"),
                icon: <CIcon file="change-password.svg"></CIcon>,
                classStyle: `m-1 btn-purple`,
                onClick: () => {
                    data.sendUnlock = true;
                    openModalFormUnlock({
                        onOk: form => {
                            onClickSave({
                                props,
                                data,
                                refs,
                                formParams: {
                                    refInspectionTemp: {
                                        ...modelRef.refInspectionTemp,
                                        ...form,
                                        isUnlock: true,
                                    },
                                },
                            });
                        }
                    });
                }
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendApprove = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: { roleName: "INSPECTION_APPROVE", isShowDepartment: true, form: modelRef },
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack
                                            ? modelRef.lastStatus.forReceiveRemark[0].remark
                                            : null,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.signApprove")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack
                                            ? modelRef.lastStatus.forReceiveRemark[0].remark
                                            : null,
                                        refStatus: record.refStatus
                                    },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    if (
        id &&
        feeStatements?.[0]?.id &&
        ((has({ menuContext, key: "forRequest" }) && ![undefined, "draft"].includes(feeStatements?.[0]?.refStatus?.key)) ||
            have({ menuContext, keys: ["forReceive", "forApprove"] }))
    ) {
        col2.push(
            createButton({
                text: `${i18n.global.t("model.feeStatement.modelName")}${feeStatements?.[0] ? ` (${feeStatements[0].refStatus?.value})` : ""}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    validate({ props, data, refs }).then(() => {
                        router.push({ name: `fees-view`, params: { id: feeStatements?.[0]?.id } });
                    });
                },
                icon: <CIcon file="active.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refInspection.*",
                "refInspectionTemp.*",
                "refInspectionTemp.refRequestDoc.*",
                "refInspectionTemp.refInformTemp.refOperation.*",
                "refInspectionTemp.refInformTemp.refInspectionLv.*",
                "refInspectionTemp.refInformTemp.refTransferDeclaration.*",
                "refInspectionTemp.refUserCompany.*",
                "refInspectionTemp.refUserCompany.refPersonType.*",
                "refInspectionTemp.refUser.*",
                "refRequestType.*",
                "refDepartment.*",
                "refStatus.*"
            ],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }],
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
